import React, { useState, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import ApiClient from '../../utils/ApiClient';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

function CompetitorMAPComplianceReport() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [competitors, setCompetitors] = useState([]);
    const [selectedCompetitors, setSelectedCompetitors] = useState(new Set());
    const [chartData, setChartData] = useState(null);
    const [filters, setFilters] = useState({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [loadingSelection, setLoadingSelection] = useState(false);

    useEffect(() => {
        fetchCompetitors();
        loadSavedSelection();
    }, []);

    useEffect(() => {
        if (selectedCompetitors.size > 0) {
            fetchChartData();
        }
    }, [selectedCompetitors, filters]);

    const fetchCompetitors = async () => {
        try {
            setLoading(true);
            const response = await ApiClient.post('/api/map-compliance-competitors', {});
            setCompetitors(response.data || []);
        } catch (err) {
            setError('Failed to load competitors');
            console.error('Error fetching competitors:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchChartData = async () => {
        try {
            setLoading(true);
            const requestData = {
                startDate: filters.startDate,
                endDate: filters.endDate,
                competitorIds: Array.from(selectedCompetitors)
            };
            
            const response = await ApiClient.post('/api/map-compliance-report', requestData);
            
            const data = response.data || [];
            setChartData(generateChartData(data));
        } catch (err) {
            setError('Failed to load chart data');
            console.error('Error fetching chart data:', err);
        } finally {
            setLoading(false);
        }
    };

    const generateChartData = (apiData) => {
        const competitorColors = [
            '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
            '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
            '#14B8A6', '#F43F5E'
        ];

        // Group by competitor name instead of ID to handle multiple IDs per competitor
        const competitorNameMap = {};
        const selectedCompetitorNames = new Set();
        
        competitors.forEach((comp, index) => {
            if (selectedCompetitors.has(comp.id)) {
                selectedCompetitorNames.add(comp.name);
                if (!competitorNameMap[comp.name]) {
                    competitorNameMap[comp.name] = {
                        name: comp.name,
                        color: competitorColors[index % competitorColors.length],
                        data: {}
                    };
                }
            }
        });

        // Aggregate data by competitor name and date
        apiData.forEach(item => {
            if (selectedCompetitorNames.has(item.competitorName)) {
                const date = item.date;
                const name = item.competitorName;
                
                if (!competitorNameMap[name].data[date]) {
                    competitorNameMap[name].data[date] = 0;
                }
                competitorNameMap[name].data[date] += item.violationCount;
            }
        });

        const allDates = [...new Set(apiData.map(item => item.date))].sort();
        
        const datasets = Object.values(competitorNameMap).map(comp => ({
            label: comp.name,
            data: allDates.map(date => comp.data[date] || 0),
            borderColor: comp.color,
            backgroundColor: comp.color + '20',
            tension: 0.1
        }));

        return {
            labels: allDates,
            datasets
        };
    };

    const handleDateRangeClick = (days) => {
        const endDate = new Date();
        const startDate = new Date(endDate.getTime() - days * 24 * 60 * 60 * 1000);
        
        setFilters({
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        });
    };

    const handleInputChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleCompetitorToggle = (competitorId) => {
        setSelectedCompetitors(prev => {
            const newSet = new Set(prev);
            const competitor = competitors.find(c => c.id === competitorId);
            
            if (competitor) {
                // Find all IDs for this competitor name
                const allIdsForName = competitors
                    .filter(c => c.name === competitor.name)
                    .map(c => c.id);
                
                if (newSet.has(competitorId)) {
                    // Remove all IDs for this competitor name
                    allIdsForName.forEach(id => newSet.delete(id));
                } else {
                    // Add all IDs for this competitor name
                    allIdsForName.forEach(id => newSet.add(id));
                }
            }
            
            return newSet;
        });
    };

    const handleSelectAll = () => {
        const filteredCompetitorIds = filteredCompetitorNames.flatMap(name => 
            competitorsByName[name].map(comp => comp.id)
        );
        
        const allSelected = filteredCompetitorIds.every(id => selectedCompetitors.has(id));
        
        if (allSelected) {
            setSelectedCompetitors(new Set());
        } else {
            setSelectedCompetitors(new Set(filteredCompetitorIds));
        }
    };

    const saveCurrentSelection = async () => {
        try {
            const selectionData = {
                mapComplianceReport: {
                    selectedCompetitorIds: Array.from(selectedCompetitors),
                    filters: filters,
                    savedAt: new Date().toISOString()
                }
            };

            const response = await ApiClient.post('/api/user/settings', {
                settings: selectionData
            });

            if (response.status === 200) {
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 3000);
            }
        } catch (err) {
            console.error('Error saving selection:', err);
            // Fallback to localStorage
            const selectionData = {
                selectedCompetitorIds: Array.from(selectedCompetitors),
                filters: filters,
                savedAt: new Date().toISOString()
            };
            localStorage.setItem('mapComplianceSelection', JSON.stringify(selectionData));
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        }
    };

    const loadSavedSelection = async () => {
        setLoadingSelection(true);
        try {
            const response = await ApiClient.get('/api/user/settings');
            const settings = response.data?.settings;
            
            if (settings?.mapComplianceReport) {
                const saved = settings.mapComplianceReport;
                
                if (saved.selectedCompetitorIds && saved.selectedCompetitorIds.length > 0) {
                    setSelectedCompetitors(new Set(saved.selectedCompetitorIds));
                }
                
                if (saved.filters) {
                    setFilters(saved.filters);
                }
                return true;
            }
        } catch (err) {
            console.error('Error loading saved selection from API:', err);
            // Fallback to localStorage
            try {
                const saved = localStorage.getItem('mapComplianceSelection');
                if (saved) {
                    const data = JSON.parse(saved);
                    
                    if (data.selectedCompetitorIds && data.selectedCompetitorIds.length > 0) {
                        setSelectedCompetitors(new Set(data.selectedCompetitorIds));
                    }
                    
                    if (data.filters) {
                        setFilters(data.filters);
                    }
                    return true;
                }
            } catch (localErr) {
                console.error('Error loading saved selection from localStorage:', localErr);
            }
        } finally {
            setLoadingSelection(false);
        }
        return false;
    };

    // Group competitors by name for display, but keep all IDs for selection
    const competitorsByName = {};
    competitors.forEach(comp => {
        if (!competitorsByName[comp.name]) {
            competitorsByName[comp.name] = [];
        }
        competitorsByName[comp.name].push(comp);
    });

    const filteredCompetitorNames = Object.keys(competitorsByName).filter(name => 
        name.toLowerCase().includes(searchTerm.toLowerCase())
    ).sort();

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'MAP Violations by Competitor Over Time'
            }
        },
        elements: {
            point: {
                radius: 0,
                hoverRadius: 6
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                title: {
                    display: true,
                    text: 'Number of Violations'
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Date'
                },
                ticks: {
                    callback: function(value, index, values) {
                        const date = new Date(this.getLabelForValue(value));
                        return date.toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric' 
                        });
                    }
                }
            }
        }
    };

    if (error) {
        return (
            <div className="text-center py-12">
                <div className="text-red-600 mb-4">{error}</div>
                <button 
                    onClick={() => window.location.reload()}
                    className="text-blue-600 hover:text-blue-800"
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Track MAP (Minimum Advertised Price) violations by competitors over time
                </h2>
                
                {/* Filters Section */}
                <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>
                    
                    {/* Quick Date Ranges */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Quick Date Ranges
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {[
                                { label: 'Last 7 Days', days: 7 },
                                { label: 'Last 30 Days', days: 30 },
                                { label: 'Last 90 Days', days: 90 },
                                { label: 'Last Year', days: 365 }
                            ].map(range => (
                                <button
                                    key={range.days}
                                    onClick={() => handleDateRangeClick(range.days)}
                                    className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                                >
                                    {range.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Custom Date Range */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Date
                            </label>
                            <input
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => handleInputChange('startDate', e.target.value)}
                                className="mt-1 block w-full pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Date
                            </label>
                            <input
                                type="date"
                                value={filters.endDate}
                                onChange={(e) => handleInputChange('endDate', e.target.value)}
                                className="mt-1 block w-full pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                            />
                        </div>
                    </div>
                </div>

                {/* Select Competitors Section */}
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">Select Competitors</h3>
                        <button
                            onClick={handleSelectAll}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                            Select All
                        </button>
                    </div>
                    
                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Search competitors..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        />
                    </div>

                    <div className="text-sm text-gray-600 mb-2">
                        {Object.values(competitorsByName).filter(comps => 
                            comps.some(comp => selectedCompetitors.has(comp.id))
                        ).length} of {filteredCompetitorNames.length} competitors selected
                    </div>

                    <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3 space-y-2">
                        {filteredCompetitorNames.map(competitorName => {
                            const competitorGroup = competitorsByName[competitorName];
                            const isSelected = competitorGroup.some(comp => selectedCompetitors.has(comp.id));
                            const representativeId = competitorGroup[0].id;
                            
                            return (
                                <label key={competitorName} className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => handleCompetitorToggle(representativeId)}
                                        className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm text-gray-700">
                                        {competitorName} 
                                        {competitorGroup.length > 1 && (
                                            <span className="text-gray-500 ml-1">({competitorGroup.length} accounts)</span>
                                        )}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* MAP Violations Chart */}
            <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">MAP Violations Chart</h3>
                    <div className="flex items-center gap-4">
                        {saveSuccess && (
                            <span className="text-green-600 text-sm font-medium">
                                Selection saved!
                            </span>
                        )}
                        <button
                            onClick={loadSavedSelection}
                            disabled={loadingSelection}
                            className={`px-4 py-2 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${
                                loadingSelection 
                                    ? 'bg-gray-400 cursor-not-allowed' 
                                    : 'bg-gray-600 hover:bg-gray-700'
                            }`}
                        >
                            {loadingSelection ? (
                                <div className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Loading...
                                </div>
                            ) : (
                                'Load Selection'
                            )}
                        </button>
                        <button
                            onClick={saveCurrentSelection}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            disabled={selectedCompetitors.size === 0}
                        >
                            Save Selection
                        </button>
                    </div>
                </div>
                
                {loading && (
                    <div className="text-center py-12">
                        <div className="text-gray-600">Loading chart data...</div>
                    </div>
                )}

                {!loading && selectedCompetitors.size === 0 && (
                    <div className="text-center py-12 text-red-600">
                        Failed to load available competitors
                    </div>
                )}

                {!loading && selectedCompetitors.size > 0 && chartData && (
                    <div style={{ height: '600px' }}>
                        <Line data={chartData} options={chartOptions} />
                    </div>
                )}

                {!loading && selectedCompetitors.size > 0 && !chartData && (
                    <div className="text-center py-12 text-gray-600">
                        No data available for the selected date range and competitors.
                    </div>
                )}
            </div>
        </div>
    );
}

export default CompetitorMAPComplianceReport;